import React, { useEffect, useState } from 'react';
import {
	Modal, ModalContent, ModalHeader, ModalBody, ModalFooter,
	Button, Textarea, Select, SelectItem
} from '@heroui/react';

/**
 * Preset cancellation reasons — legacy parity with `cancelReasonOptions`
 * in `source/js/modules/calendar/calendar.consts.js:201-215`. `value` is
 * sent verbatim to backend as `reason`. The `'other'` row triggers a
 * free-text Textarea where the user's input replaces the value code as
 * the submitted reason.
 */
const CANCEL_REASON_OPTIONS = [
	{ value: 'weather', label: 'Weather' },
	{ value: 'mechanical', label: 'Mechanical' },
	{ value: 'medical', label: 'Medical' },
	{ value: 'forceMajeure', label: 'Force Majeure' },
	{ value: 'maintenance', label: 'Maintenance' },
	{ value: 'aircraftIsUnavailable', label: 'Aircraft is Unavailable' },
	{ value: 'weatherNewRegulations', label: 'Weather - New Regulations' },
	{ value: 'other', label: 'Other' }
];

/**
 * Scope options for cancelling a recurring event — legacy parity with
 * `cancelActionOptions`. NO `all` entry (delete supports it, cancel does
 * not).
 */
const CANCEL_RECURRING_OPTIONS = [
	{ value: 'current', label: 'Just this event' },
	{ value: 'not-started', label: 'Not yet started events' },
	{ value: 'following', label: 'This and all following events' }
];

const OTHER_REASON_MIN = 10;
const OTHER_REASON_MAX = 1000;

/**
 * CancelEventConfirm — required-reason Select with conditional free-text
 * Textarea for the `'other'` pick, and a 3-option scope Select for
 * recurring events with `canManageRecurrent`.
 *
 * @param {boolean} isOpen
 * @param {() => void} onClose
 * @param {(payload: { reason: string, scope: string|null }) => Promise<void>} onConfirm
 * @param {boolean} showScope When true, render the recurring scope dropdown.
 * @param {string} eventTitle Optional — surfaced in the dialog copy.
 */
function CancelEventConfirm({ isOpen, onClose, onConfirm, showScope, eventTitle }) {
	const [reasonKey, setReasonKey] = useState('');
	const [otherText, setOtherText] = useState('');
	const [scope, setScope] = useState('current');
	const [submitting, setSubmitting] = useState(false);
	const [reasonError, setReasonError] = useState('');

	useEffect(() => {
		if (isOpen) {
			setReasonKey('');
			setOtherText('');
			setScope('current');
			setSubmitting(false);
			setReasonError('');
		}
	}, [isOpen]);

	const handleConfirm = async () => {
		if (!reasonKey) {
			setReasonError('Reason is required');
			return;
		}
		let finalReason;
		if (reasonKey === 'other') {
			const trimmed = otherText.trim();
			if (trimmed.length < OTHER_REASON_MIN) {
				setReasonError(`At least ${OTHER_REASON_MIN} characters required`);
				return;
			}
			finalReason = trimmed;
		} else {
			finalReason = reasonKey;
		}
		setSubmitting(true);
		try {
			await onConfirm({ reason: finalReason, scope: showScope ? scope : null });
		} finally {
			setSubmitting(false);
		}
	};

	const isOther = reasonKey === 'other';

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="md" placement="center">
			<ModalContent>
				<ModalHeader className="flex flex-col gap-1 border-b border-default-200">
					<span className="text-base font-semibold">Cancel Event</span>
					{eventTitle && (
						<span className="text-xs text-default-500 font-normal truncate">{eventTitle}</span>
					)}
				</ModalHeader>
				<ModalBody className="pt-4 space-y-3">
					<Select
						size="sm"
						label="Reason"
						placeholder="Select reason"
						aria-label="Cancellation reason"
						selectedKeys={reasonKey ? [reasonKey] : []}
						onSelectionChange={keys => {
							const first = keys === 'all' ? null : Array.from(keys)[0];
							setReasonKey(first ? String(first) : '');
							if (reasonError) setReasonError('');
						}}
						isInvalid={!isOther && !!reasonError}
						errorMessage={!isOther ? reasonError : undefined}
						isRequired
					>
						{CANCEL_REASON_OPTIONS.map(o => (
							<SelectItem key={o.value}>{o.label}</SelectItem>
						))}
					</Select>
					{isOther && (
						<Textarea
							aria-label="Other cancellation reason"
							label="Please specify"
							placeholder={`Enter at least ${OTHER_REASON_MIN} characters`}
							value={otherText}
							minRows={3}
							maxLength={OTHER_REASON_MAX}
							onValueChange={v => {
								setOtherText(v);
								if (reasonError && v.trim().length >= OTHER_REASON_MIN) setReasonError('');
							}}
							isInvalid={!!reasonError}
							errorMessage={reasonError}
							isRequired
						/>
					)}
					{showScope && (
						<Select
							size="sm"
							label="Apply to"
							aria-label="Cancellation scope"
							selectedKeys={[scope]}
							onSelectionChange={keys => {
								const first = keys === 'all' ? null : Array.from(keys)[0];
								if (first) setScope(String(first));
							}}
						>
							{CANCEL_RECURRING_OPTIONS.map(o => (
								<SelectItem key={o.value}>{o.label}</SelectItem>
							))}
						</Select>
					)}
				</ModalBody>
				<ModalFooter className="border-t border-default-200">
					<Button size="sm" variant="bordered" onPress={onClose} isDisabled={submitting}>
						Back
					</Button>
					<Button size="sm" color="warning" onPress={handleConfirm} isLoading={submitting}>
						Cancel event
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
}

export default CancelEventConfirm;
export { CANCEL_REASON_OPTIONS, CANCEL_RECURRING_OPTIONS };
